import { useState, useMemo, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Sidebar, type SidebarMenuItem } from './Sidebar';
import { TopNav } from './TopNav';
import Breadcrumbs from '@components/shared/Breadcrumbs/Breadcrumbs';
import NotificationContainer from '@components/shared/NotificationContainer/NotificationContainer';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useTranslation } from '@/hooks/useTranslation';
import { useNotificationStream } from '@/hooks/useNotificationStream';
import { incrementUnreadCountInCache, prependNotificationToCache } from '@/hooks/useNotifications';
import { queryClient } from '@/lib/queryClient';
import { getGroupItems } from '@/services/navigationService';
import {
  getGroupChildRoutes,
  getParentGroupLink,
  getRouteForModule,
} from '@/lib/navigationRegistry';
import type { EffectiveNavigationResponse, NavigationEntry } from '@/types/navigation';
import type { OrganizationType } from '@/types/session';
import EmployeesIcon from '@assets/icons/EmployeesIcon';
import ConfigurationIcon from '@assets/icons/ConfigurationIcon';
import DashboardIcon from '@assets/icons/DashboardIcon';
import EntitiesIcon from '@assets/icons/EntitiesIcon';
import OrgProfileIcon from '@assets/icons/OrgProfileIcon';
import FolderIcon from '@assets/icons/FolderIcon';
import ArrowLeftIcon from '@assets/icons/ArrowLeftIcon';
import { EntitySessionBanner } from '@components/shared/EntitySessionBanner/EntitySessionBanner';

// ============================================
// App Shell — Sidebar + TopNav + Content area
// Uses global layout classes from _layout.scss
// ============================================

// Backend `icon` field (e.g. "settings", "people", "folder") → React node.
// Falls back to identifier-based resolution, then to the people icon.
const API_ICONS: Record<string, ReactNode> = {
  settings: <ConfigurationIcon />,
  people: <EmployeesIcon />,
  briefcase: <EntitiesIcon />,
  building: <OrgProfileIcon />,
  home: <DashboardIcon />,
  folder: <FolderIcon />,
  // No dedicated icon today — reuse the briefcase. Swap when a Contracts icon
  // ships in @assets/icons.
  assignment: <EntitiesIcon />,
};

const IDENTIFIER_ICONS: Record<string, ReactNode> = {
  CATEGORY_CONFIGURATION: <ConfigurationIcon />,
  CATEGORY_SETTINGS: <ConfigurationIcon />,
  CATEGORY_EMPLOYEE_DOCUMENTS: <FolderIcon />,
  CATEGORY_DOCUMENTS: <FolderIcon />,
  CATEGORY_CONTRACTS: <EntitiesIcon />,
  MODULE_TENANT_MANAGEMENT: <EmployeesIcon />,
  MODULE_ENTITY_MANAGEMENT: <EntitiesIcon />,
  MODULE_EMPLOYEES: <EmployeesIcon />,
  MODULE_TENANT_PROFILE: <OrgProfileIcon />,
};

function resolveIcon(identifier: string, apiIcon?: string): ReactNode {
  if (apiIcon && API_ICONS[apiIcon]) return API_ICONS[apiIcon];
  return IDENTIFIER_ICONS[identifier] ?? <EmployeesIcon />;
}

// ── Build sidebar items from navigation API ──

type TFn = (key: string, fallback: string) => string;

function buildHomeItem(orgType: OrganizationType, t: TFn): SidebarMenuItem {
  let href: string;
  switch (orgType) {
    case 'WORKPAY':
      href = '/w/home';
      break;
    case 'TENANT':
      href = '/t/home';
      break;
    case 'PARTNER':
      href = '/p';
      break;
  }
  return {
    id: 'module_home',
    label: t('K_SIDEBAR_HOME', 'Home'),
    icon: <DashboardIcon />,
    href,
  };
}

function navHasHome(entries: NavigationEntry[]): boolean {
  // GROUP entries render as single sidebar links (NOT expandable), so the
  // flat identifier check is correct for both ITEM and GROUP. We never
  // need to drill into GROUP.items for the home check — Home is its own
  // top-level entry (MODULE_HOME, type ITEM).
  return entries.some((e) => e.identifier === 'MODULE_HOME');
}

function buildMenuItemsFromNav(
  entries: NavigationEntry[],
  orgType: OrganizationType,
  t: TFn,
): SidebarMenuItem[] {
  const items: SidebarMenuItem[] = [];

  for (const entry of entries) {
    const href = getRouteForModule(entry.identifier, orgType);
    if (!href) continue;
    items.push({
      id: entry.identifier.toLowerCase(),
      label: t(entry.displayKey, entry.displayKey),
      icon: resolveIcon(entry.identifier, entry.icon),
      href,
      // GROUP entries carry their identifier so AppShell can prefetch their
      // sub-modules and landing pages can identify which group they belong to.
      ...(entry.type === 'GROUP' ? { groupIdentifier: entry.identifier } : {}),
    });
  }

  return items;
}

// ── Fallback: static sidebar items (used when API hasn't loaded yet) ──

function getStaticMenuItems(
  orgType: OrganizationType | null,
  userType: string | null | undefined,
  t: TFn,
): SidebarMenuItem[] {
  if (userType === 'EMPLOYEE') {
    return [
      { id: 'home', label: t('K_SIDEBAR_HOME', 'Home'), icon: <DashboardIcon />, href: '/e' },
    ];
  }
  switch (orgType) {
    case 'WORKPAY':
      return [
        {
          id: 'module_home',
          label: t('K_SIDEBAR_HOME', 'Home'),
          icon: <DashboardIcon />,
          href: '/w/home',
        },
        {
          id: 'module_tenant_management',
          label: t('K_SIDEBAR_TENANTS', 'Clients'),
          icon: <EmployeesIcon />,
          href: '/w/clients',
        },
        {
          id: 'module_entity_management',
          label: t('K_SIDEBAR_ENTITIES', 'Legal Entities'),
          icon: <EntitiesIcon />,
          href: '/w/entities',
        },
        {
          id: 'module_employees',
          label: t('K_SIDEBAR_EMPLOYEES', 'Employees'),
          icon: <EmployeesIcon />,
          href: '/w/employees',
        },
        {
          id: 'category_employee_documents',
          label: t('K_SIDEBAR_DOCUMENTS', 'Documents'),
          icon: <FolderIcon />,
          href: '/w/documents',
        },
        {
          id: 'module_document_snapshots',
          label: t('K_SIDEBAR_CONTRACTS', 'Contracts'),
          icon: <EntitiesIcon />,
          href: '/w/contracts',
        },
        {
          id: 'category_configuration',
          label: t('K_SIDEBAR_SETTINGS', 'Settings'),
          icon: <ConfigurationIcon />,
          href: '/w/settings',
        },
      ];
    case 'TENANT':
      return [
        {
          id: 'module_home',
          label: t('K_SIDEBAR_HOME', 'Home'),
          icon: <DashboardIcon />,
          href: '/t/home',
        },
        {
          id: 'module_employees',
          label: t('K_SIDEBAR_EMPLOYEES', 'Employees'),
          icon: <EmployeesIcon />,
          href: '/t/employees',
        },
        {
          id: 'category_employee_documents',
          label: t('K_SIDEBAR_DOCUMENTS', 'Documents'),
          icon: <FolderIcon />,
          href: '/t/documents',
        },
        {
          id: 'module_document_snapshots',
          label: t('K_SIDEBAR_CONTRACTS', 'Contracts'),
          icon: <EntitiesIcon />,
          href: '/t/contracts',
        },
        {
          id: 'category_configuration',
          label: t('K_SIDEBAR_SETTINGS', 'Settings'),
          icon: <ConfigurationIcon />,
          href: '/t/settings',
        },
      ];
    case 'PARTNER':
      return [
        { id: 'home', label: t('K_SIDEBAR_HOME', 'Home'), icon: <DashboardIcon />, href: '/p' },
      ];
    default:
      return [];
  }
}

// ── Active item resolver ──

function getActiveItemFromPath(
  pathname: string,
  menuItems: SidebarMenuItem[],
  bottomItems: SidebarMenuItem[] | undefined,
  orgType: OrganizationType,
): string {
  const allItems = [...menuItems, ...(bottomItems ?? [])];

  // Pass 1 — child items (most specific).
  for (const item of allItems) {
    if (item.children) {
      for (const child of item.children) {
        if (pathname.startsWith(child.href)) return child.id;
      }
    }
  }

  // Pass 2 — direct prefix match on top-level item hrefs. Longest href first
  // so e.g. `/w/settings` wins over `/w` if both ever exist.
  const sorted = [...allItems]
    .filter((i) => !!i.href)
    .sort((a, b) => (b.href?.length ?? 0) - (a.href?.length ?? 0));
  for (const item of sorted) {
    if (item.href && pathname.startsWith(item.href)) return item.id;
  }

  // Pass 3 — group-child highlight. A child module route (e.g.
  // /w/employment-types) doesn't share a URL prefix with its parent group's
  // landing (/w/settings), so highlight the GROUP parent when the current
  // path matches any of the group's registered child routes.
  // GROUP items carry `groupIdentifier` set by buildMenuItemsFromNav — no
  // hardcoded mapping needed.
  for (const item of allItems) {
    if (!item.groupIdentifier) continue;
    const childRoutes = getGroupChildRoutes(item.groupIdentifier, orgType);
    if (childRoutes.some((r) => pathname.startsWith(r))) return item.id;
  }

  return allItems[0]?.id ?? '';
}

// ── Bottom items: prefer API `sidebar-bottom`, fall back to hardcoded list ──

function getTenantBottomItems(t: TFn): SidebarMenuItem[] {
  return [
    {
      id: 'org-profile',
      label: t('K_SIDEBAR_ORGANIZATION_PROFILE', 'Company Profile'),
      icon: <OrgProfileIcon />,
      href: '/t/profile/self',
    },
  ];
}

// ============================================
// COMPONENT
// ============================================

export interface AppShellProps {
  children: ReactNode;
  /** Sidebar nav from API; owned by __root.tsx. Undefined → static fallback. */
  effectiveNav?: EffectiveNavigationResponse;
}

export function AppShell({ children, effectiveNav }: AppShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Mobile drawer auto-closes on route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Mobile drawer closes on Escape.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  // Return focus to the hamburger trigger when the drawer closes — only on a
  // true→false transition, not on initial mount.
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const prevMobileOpen = useRef(false);
  useEffect(() => {
    if (prevMobileOpen.current && !mobileOpen) {
      triggerRef.current?.focus();
    }
    prevMobileOpen.current = mobileOpen;
  }, [mobileOpen]);

  const organizationType = useAuthStore((s) => s.organizationType);
  const isEntitySession = useAuthStore((s) => s.isEntitySession);
  const userType = useAuthStore((s) => s.userType);
  const session = useAuthStore((s) => s.session);
  const { t } = useTranslation();

  const isEmployee = userType === 'EMPLOYEE' || pathname.startsWith('/e');

  // SSE real-time notifications.
  // Inject the new row into the infinite query cache and bump the unread-count
  // cache directly — avoids refetching every loaded page and keeps the badge
  // in sync without a store mirror. The cache is the single source of truth.
  useNotificationStream({
    enabled: !!session,
    onNotification: (notification) => {
      prependNotificationToCache(queryClient, notification);
      incrementUnreadCountInCache(queryClient);

      useNotificationStore.getState().addNotification({
        variant: 'informational',
        title: notification.title,
        description: notification.body,
      });
    },
  });

  // Render whatever the effective navigation API returns. No client-side
  // permission filtering — the API is authoritative. Route guards in
  // routes/w.tsx and routes/t.tsx still block URL access for unauthorized users.
  // Home is prepended to the top section only (the static fallback already
  // leads with Home), unless the backend already seeded MODULE_HOME — a
  // forward-compatible dedupe so we never render a duplicate Home entry.
  const menuItems = useMemo(() => {
    if (!organizationType) return [];

    const sidebarTopEntries = effectiveNav?.['sidebar-top'];
    if (sidebarTopEntries && sidebarTopEntries.length > 0 && organizationType !== 'PARTNER') {
      const built = buildMenuItemsFromNav(sidebarTopEntries, organizationType, t);
      if (!navHasHome(sidebarTopEntries)) {
        built.unshift(buildHomeItem(organizationType, t));
      }
      return built;
    }
    return getStaticMenuItems(organizationType, isEmployee ? 'EMPLOYEE' : userType, t);
  }, [effectiveNav, organizationType, isEmployee, userType, t]);

  // Prefetch sub-module lists for all GROUP entries. staleTime: Infinity means
  // these are fetched once and cached for the session — landing pages (Settings,
  // Documents, Contracts) pick them up instantly from the cache on navigation.
  const groupIdentifiers = useMemo(
    () => menuItems.map((i) => i.groupIdentifier).filter((id): id is string => !!id),
    [menuItems],
  );

  const prefetchGroups = useCallback(() => {
    for (const id of groupIdentifiers) {
      void queryClient.prefetchQuery({
        queryKey: ['effective', 'groups', id],
        queryFn: () => getGroupItems(id),
        staleTime: Infinity,
      });
    }
  }, [groupIdentifiers]);

  useEffect(() => {
    if (groupIdentifiers.length > 0) prefetchGroups();
  }, [groupIdentifiers, prefetchGroups]);

  // Bottom items: prefer the `sidebar-bottom` section from the navigation API.
  // If the API returns it (e.g. MODULE_TENANT_PROFILE seeded in V1__module_config_seed.js),
  // render it through the same builder used for `sidebar-top`. If absent, fall back to the
  // hardcoded tenant-only list so the Organization Profile entry never disappears mid-rollout.
  const bottomItems = useMemo(() => {
    if (!organizationType || isEmployee) return undefined;
    const apiBottom = effectiveNav?.['sidebar-bottom'];
    if (apiBottom && apiBottom.length > 0) {
      const built = buildMenuItemsFromNav(apiBottom, organizationType, t);
      if (built.length > 0) return built;
    }
    if (organizationType === 'TENANT') return getTenantBottomItems(t);
    return undefined;
  }, [effectiveNav, organizationType, isEmployee, t]);

  const activeItemId = getActiveItemFromPath(
    pathname,
    menuItems,
    bottomItems,
    organizationType ?? 'TENANT',
  );

  const hideBreadcrumbs = pathname === '/t' || pathname.startsWith('/t/');

  // Render a "Back to <group>" link above page content whenever the current
  // pathname is a registered child of any navigation group (Settings, Contracts,
  // Documents…). Tenant routes hide the breadcrumb bar entirely (see
  // hideBreadcrumbs above), so without this the user has no in-page way back
  // to the parent landing. The helper iterates GROUP_CHILD_IDENTIFIERS in
  // navigationRegistry, so adding a new group is registry-only — no AppShell
  // change required.
  const parentGroupLink = useMemo(() => {
    if (!organizationType || organizationType === 'PARTNER') return null;
    return getParentGroupLink(pathname, organizationType);
  }, [organizationType, pathname]);

  return (
    <div className={`app-shell${isEntitySession ? ' app-shell--entity-session' : ''}`}>
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((prev) => !prev)}
        menuItems={menuItems}
        activeItemId={activeItemId}
        bottomItems={bottomItems}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div
        className={`sidebar-overlay${mobileOpen ? ' sidebar-overlay--visible' : ''}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      <TopNav
        collapsed={sidebarCollapsed}
        onMobileMenuClick={() => setMobileOpen(true)}
        mobileMenuTriggerRef={triggerRef}
      />

      <EntitySessionBanner />
      {isEntitySession && <div className="entity-session-viewport-ring" aria-hidden="true" />}

      <main className="main-content">
        {!hideBreadcrumbs && <Breadcrumbs />}
        {parentGroupLink && (
          <div className="back-to-group">
            <Link to={parentGroupLink.href} className="back-to-group__link">
              <ArrowLeftIcon className="back-to-group__icon" />
              {t(parentGroupLink.labelKey, parentGroupLink.labelFallback)}
            </Link>
          </div>
        )}
        {children}
      </main>

      <NotificationContainer />
    </div>
  );
}
